import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { FabricBoundingRect } from '~/static/types/align'
import { applyAlpha } from '~/utils/ColorHelper'

export const calculateZoom = (
  objectWidth: number,
  objectHeight: number,
  containerWidth: number,
  containerHeight: number,
): number => {
  return Math.min(containerWidth / objectWidth, containerHeight / objectHeight, 1)
}

export const makeShadow = (props: {
  flipX: boolean
  flipY: boolean
  angle: number
  shadow: boolean
  shadowColor: string
  shadowOpacity: number
  shadowX: number
  shadowY: number
  shadowBlur: number
  nonScaling?: boolean
}): fabricTypes.Shadow | null => {
  if (!props.shadow) {
    return null
  }

  const isNode = typeof window === 'undefined'

  // На ноде он тень применяет до поворота и флипа, а в браузере - после. Поэтому тут мы
  // поворачиваем и отражаем тень самостоятельно, чтобы привести к формату браузера
  if (isNode) {
    const currentShadow = new fabric.Point({
      x: props.flipX ? -props.shadowX : props.shadowX,
      y: props.flipY ? -props.shadowY : props.shadowY,
    })
    
    // Вращаем вектор тени в обратную сторону от угла объекта
    const rads = fabric.util.degreesToRadians(props.flipX === props.flipY ? -props.angle : props.angle)
    const rotated = fabric.util.rotateVector(currentShadow, rads)
  
    return new fabric.Shadow({
      blur: props.shadowBlur,
      offsetX: rotated.x,
      offsetY: rotated.y,
      color: applyAlpha(props.shadowColor, props.shadowOpacity),
      nonScaling: props.nonScaling ?? true,
    })
  }

  return new fabric.Shadow({
    blur: props.shadowBlur,
    offsetX: props.shadowX,
    offsetY: props.shadowY,
    color: applyAlpha(props.shadowColor, props.shadowOpacity),
    nonScaling: props.nonScaling ?? true,
  })
}

export const getBoundingRect = (object: fabricTypes.FabricObject): FabricBoundingRect => {
  const strokeX = (object.strokeUniform ? object.strokeWidth : (object.strokeWidth * object.scaleX)) / 2
  const strokeY = (object.strokeUniform ? object.strokeWidth : (object.strokeWidth * object.scaleY)) / 2

  const lt = new fabric.Point(object.left - strokeX, object.top - strokeY)

  const lb = new fabric.Point(lt.x, lt.y + getScaledHeight(object) + strokeY)
  const rt = new fabric.Point(lt.x + getScaledWidth(object) + strokeX, lt.y)
  const rb = new fabric.Point(rt.x + strokeX, lb.y + strokeY)

  const lbRotated = lb.rotate((object.angle) * (Math.PI / 180), lt)
  const rtRotated = rt.rotate((object.angle) * (Math.PI / 180), lt)
  const rbRotated = rb.rotate((object.angle) * (Math.PI / 180), lt)

  const objectTop = Math.min(lt.y, lbRotated.y, rtRotated.y, rbRotated.y)
  const objectBottom = Math.max(lt.y, lbRotated.y, rtRotated.y, rbRotated.y)
  const objectLeft = Math.min(lt.x, lbRotated.x, rtRotated.x, rbRotated.x)
  const objectRight = Math.max(lt.x, lbRotated.x, rtRotated.x, rbRotated.x)

  return {
    left: objectLeft,
    top: objectTop,
    width: objectRight - objectLeft,
    height: objectBottom - objectTop,
  }
}

const getScaledHeight = (object: fabricTypes.FabricObject): number => {
  return object.height * object.scaleY * (object.group?.scaleY ?? 1)
}

const getScaledWidth = (object: fabricTypes.FabricObject): number => {
  return object.width * object.scaleX * (object.group?.scaleX ?? 1)
}
